import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { ErrorNotice, Field, Notice, Spinner, formatMoney } from '../components/ui';
import type { JoinCategory, JoinPricing, JoinResult } from '../types';

/**
 * The public application form. One long page with clear sections and a
 * running total, not a multi-step wizard — fewer taps, and nothing is lost
 * if someone has to go back and fix a field, which matters more on a phone
 * than a progress bar does.
 *
 * Everything shown here — prices, BDI bands, whether applications are even
 * open — comes from the server on load and is recomputed there again on
 * submit. Nothing calculated on this page is trusted; it exists only so the
 * applicant can see the total before they commit to it.
 */
export function JoinPage() {
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');

  const [pricing, setPricing] = useState<JoinPricing | null>(null);
  const [pricingError, setPricingError] = useState<unknown>(null);
  const [invitedBy, setInvitedBy] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [result, setResult] = useState<JoinResult | null>(null);

  useEffect(() => {
    api
      .get<JoinPricing>('/join/pricing')
      .then(setPricing)
      .catch(setPricingError);
  }, []);

  useEffect(() => {
    if (!inviteToken) return;
    api
      .get<{ inviting_member_name: string }>(`/join/invite/${inviteToken}`)
      .then((r) => setInvitedBy(r.inviting_member_name))
      .catch((failure) => {
        setInviteError(
          failure instanceof ApiError ? failure.message : 'That invite link could not be checked.',
        );
      });
  }, [inviteToken]);

  if (pricingError !== null) {
    return (
      <JoinShell>
        <ErrorNotice error={pricingError} />
      </JoinShell>
    );
  }

  if (pricing === null) {
    return (
      <JoinShell>
        <Spinner label="Loading" />
      </JoinShell>
    );
  }

  if (!pricing.open) {
    return (
      <JoinShell>
        <Notice kind="info">
          Applications aren't open at the moment. Please check back soon, or get in touch with the
          committee if you think that's wrong.
        </Notice>
      </JoinShell>
    );
  }

  if (result !== null) {
    return (
      <JoinShell>
        <ApplicationReceived result={result} />
      </JoinShell>
    );
  }

  if (inviteToken && inviteError !== null) {
    return (
      <JoinShell>
        <Notice kind="error">{inviteError}</Notice>
        <p className="muted small">
          Ask them to send you a new invite link, or{' '}
          <Link to="/join">apply for a different kind of membership</Link>.
        </p>
      </JoinShell>
    );
  }

  if (inviteToken && invitedBy === null) {
    return (
      <JoinShell>
        <Spinner label="Checking your invite" />
      </JoinShell>
    );
  }

  return (
    <JoinShell>
      <ApplicationForm pricing={pricing} inviteToken={inviteToken} invitedBy={invitedBy} onDone={setResult} />
    </JoinShell>
  );
}

function JoinShell({ children }: { children: ReactNode }) {
  return (
    <div className="join-shell">
      <header className="join-header">
        <span className="brand">
          Demo Beekeepers <span className="muted">Association</span>
        </span>
        <Link to="/login" className="join-signin">
          Already a member? Sign in
        </Link>
      </header>
      <main className="join-content">{children}</main>
    </div>
  );
}

function ApplicationReceived({ result }: { result: JoinResult }) {
  const { member, subscription } = result;
  return (
    <div className="card">
      <h1>Thanks, {member.first_name}</h1>
      <Notice kind="success">Your application has been received.</Notice>

      <dl className="details details-spaced">
        <div className="detail">
          <dt>Membership</dt>
          <dd>
            {subscription.category_name}, {subscription.year}
          </dd>
        </div>
        {subscription.colony_count !== null ? (
          <div className="detail">
            <dt>Colonies declared</dt>
            <dd>{subscription.colony_count}</dd>
          </div>
        ) : null}
        <div className="detail">
          <dt>Total due</dt>
          <dd>
            <strong>{formatMoney(subscription.total_due)}</strong>
          </dd>
        </div>
      </dl>

      {subscription.needs_bdi_referral ? (
        <Notice kind="info">
          You declared more colonies than our standard Bee Diseases Insurance bands cover — the
          committee will be in touch separately about cover for your apiary.
        </Notice>
      ) : null}

      <p>
        The committee will be in touch with how to pay. Please use your surname as the payment
        reference if paying by bank transfer.
      </p>

      {member.has_login ? (
        <p className="muted small">
          You can <Link to="/login">sign in</Link> any time to see your membership details.
        </p>
      ) : null}
    </div>
  );
}

interface FormState {
  category_code: string;
  title: string;
  first_name: string;
  last_name: string;
  address_1: string;
  address_2: string;
  town: string;
  postcode: string;
  telephone: string;
  mobile: string;
  email: string;
  date_of_birth: string;
  guardian_name: string;
  guardian_email: string;
  colony_count: string;
  password: string;
  comments: string;
}

const EMPTY_FORM: FormState = {
  category_code: '',
  title: '',
  first_name: '',
  last_name: '',
  address_1: '',
  address_2: '',
  town: '',
  postcode: '',
  telephone: '',
  mobile: '',
  email: '',
  date_of_birth: '',
  guardian_name: '',
  guardian_email: '',
  colony_count: '',
  password: '',
  comments: '',
};

function ageFromDob(dob: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function ApplicationForm({
  pricing,
  inviteToken,
  invitedBy,
  onDone,
}: {
  pricing: Extract<JoinPricing, { open: true }>;
  inviteToken: string | null;
  invitedBy: string | null;
  onDone: (result: JoinResult) => void;
}) {
  const isPartnerInvite = inviteToken !== null && invitedBy !== null;
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY_FORM,
    category_code: isPartnerInvite ? 'partner' : '',
  }));
  const [donationAmounts, setDonationAmounts] = useState<Record<number, string>>({});
  const [giftAid, setGiftAid] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  const category: JoinCategory | undefined = useMemo(
    () => pricing.categories.find((c) => c.code === form.category_code),
    [pricing.categories, form.category_code],
  );

  const age = form.date_of_birth ? ageFromDob(form.date_of_birth) : null;
  const needsGuardian = category?.max_age !== undefined && category?.max_age !== null
    ? true
    : age !== null && age < 18;
  const eligibleForLogin = form.email.trim() !== '' && (!needsGuardian || (age !== null && age >= 13));

  const bdiAmount = useMemo(() => {
    if (!category?.carries_bdi) return null;
    const count = Number(form.colony_count || '0');
    const band = pricing.bdi_bands.find((b) => b.max_colonies >= count);
    return band ? band.premium : 'referral';
  }, [category, form.colony_count, pricing.bdi_bands]);

  const donationsTotal = Object.values(donationAmounts).reduce((sum, value) => {
    const n = Number(value);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);

  const runningTotal =
    (category?.amount ?? 0) + (typeof bdiAmount === 'number' ? bdiAmount : 0) + donationsTotal;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!category) {
      setError(new Error('Please choose a type of membership.'));
      return;
    }

    setBusy(true);
    try {
      const donations = Object.entries(donationAmounts)
        .map(([fundId, amount]) => ({ fund_id: Number(fundId), amount: Number(amount) }))
        .filter((d) => d.amount > 0);

      const result = await api.post<JoinResult>('/join', {
        category_code: form.category_code,
        title: form.title || null,
        first_name: form.first_name,
        last_name: form.last_name,
        address_1: form.address_1 || null,
        address_2: form.address_2 || null,
        town: form.town || null,
        postcode: form.postcode || null,
        telephone: form.telephone || null,
        mobile: form.mobile || null,
        email: form.email || null,
        date_of_birth: form.date_of_birth || null,
        guardian_name: needsGuardian ? form.guardian_name : null,
        guardian_email: needsGuardian ? form.guardian_email : null,
        colony_count: category.carries_bdi ? Number(form.colony_count || '0') : null,
        password: eligibleForLogin ? form.password : null,
        comments: form.comments || null,
        donations,
        gift_aid: pricing.gift_aid ? giftAid : false,
        invite_token: inviteToken,
      });
      onDone(result);
    } catch (failure) {
      setError(failure);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="join-form" onSubmit={onSubmit}>
      <h1>Join Demo Beekeepers Association</h1>
      <p className="muted">
        Fill this in on your phone or a computer, in one go — it takes about five minutes.
      </p>

      <ErrorNotice error={error} />

      <section className="card">
        <h2>Membership type</h2>

        {isPartnerInvite ? (
          <Notice kind="info">You're joining as {invitedBy}'s partner member.</Notice>
        ) : (
          <div className="category-choices" role="radiogroup" aria-label="Membership type">
            {pricing.categories
              .filter((c) => !c.requires_parent)
              .map((c) => (
                <label key={c.code} className={`category-choice ${form.category_code === c.code ? 'on' : ''}`}>
                  <input
                    type="radio"
                    name="category"
                    value={c.code}
                    checked={form.category_code === c.code}
                    onChange={() => set('category_code', c.code)}
                  />
                  <span className="category-name">{c.name}</span>
                  <span className="category-price">{formatMoney(c.amount)}</span>
                </label>
              ))}
          </div>
        )}
        <p className="muted small">
          Already a Full Member and want to add your partner? Ask them to sign in and generate an
          invite link from their own account — that's the only way to join as a Partner Member.
        </p>
      </section>

      <section className="card">
        <h2>Your details</h2>
        <div className="grid">
          <Field label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
          <Field
            label="First name"
            required
            value={form.first_name}
            onChange={(e) => set('first_name', e.target.value)}
          />
          <Field
            label="Last name"
            required
            value={form.last_name}
            onChange={(e) => set('last_name', e.target.value)}
          />
          <Field
            label="Address line 1"
            value={form.address_1}
            onChange={(e) => set('address_1', e.target.value)}
          />
          <Field
            label="Address line 2"
            value={form.address_2}
            onChange={(e) => set('address_2', e.target.value)}
          />
          <Field label="Town" value={form.town} onChange={(e) => set('town', e.target.value)} />
          <Field
            label="Postcode"
            value={form.postcode}
            onChange={(e) => set('postcode', e.target.value)}
          />
          <Field
            label="Telephone"
            value={form.telephone}
            onChange={(e) => set('telephone', e.target.value)}
          />
          <Field label="Mobile" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
          <Field
            label="Email"
            type="email"
            hint="Used for renewal reminders. Leave blank if you don't have one."
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Field
            label="Date of birth"
            type="date"
            required={category?.max_age !== undefined && category?.max_age !== null}
            hint={category?.max_age != null ? 'Required for Junior membership.' : undefined}
            value={form.date_of_birth}
            onChange={(e) => set('date_of_birth', e.target.value)}
          />
        </div>
      </section>

      {needsGuardian ? (
        <section className="card">
          <h2>Parent or guardian</h2>
          <p className="muted small">
            Required for every applicant under 18, whatever type of membership they're joining as.
          </p>
          <div className="grid">
            <Field
              label="Guardian's name"
              required
              value={form.guardian_name}
              onChange={(e) => set('guardian_name', e.target.value)}
            />
            <Field
              label="Guardian's email"
              type="email"
              required
              value={form.guardian_email}
              onChange={(e) => set('guardian_email', e.target.value)}
            />
          </div>
        </section>
      ) : null}

      {category?.carries_bdi ? (
        <section className="card">
          <h2>Colonies &amp; Bee Diseases Insurance</h2>
          <p className="muted small">
            The first few colonies are normally covered at no extra cost. Declaring more than{' '}
            {pricing.bdi_bands.at(-1)?.max_colonies ?? 'the largest band'} means the committee will
            follow up separately about cover, rather than the form failing.
          </p>
          <Field
            label="Number of colonies"
            type="number"
            min={0}
            value={form.colony_count}
            onChange={(e) => set('colony_count', e.target.value)}
          />
          {bdiAmount === 'referral' ? (
            <Notice kind="info">
              That's more than our standard bands cover — we'll be in touch about your BDI cover
              separately. It won't stop your application.
            </Notice>
          ) : bdiAmount !== null ? (
            <p className="muted small">BDI premium: {formatMoney(bdiAmount)}</p>
          ) : null}
        </section>
      ) : null}

      <section className="card">
        <h2>Donations</h2>
        <p className="muted small">Optional — leave blank to skip.</p>
        <div className="grid">
          {pricing.donation_funds.map((fund) => (
            <Field
              key={fund.id}
              label={fund.name}
              type="number"
              min={0}
              step={0.01}
              value={donationAmounts[fund.id] ?? ''}
              onChange={(e) =>
                setDonationAmounts((previous) => ({ ...previous, [fund.id]: e.target.value }))
              }
            />
          ))}
        </div>

        {/*
          Only shown when the association actually claims Gift Aid — the
          committee turns this on under Rates & settings.

          A tickbox on its own is not a Gift Aid declaration. HMRC requires the
          donor to confirm they are a UK taxpayer and that they understand they
          must have paid enough tax to cover what is reclaimed, so the wording
          has to be in front of them when they tick it.
        */}
        {pricing.gift_aid ? (
          <div className="declaration">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={giftAid}
                onChange={(e) => setGiftAid(e.target.checked)}
              />
              <span>
                <strong>Gift Aid — boost your donation by 25p in every £1, at no cost to you.</strong>
                <br />
                I want the Demo Beekeepers Association to treat this and any donations I
                make in the future, or have made in the past four years, as Gift Aid donations. I am
                a UK taxpayer and I understand that if I pay less Income Tax and/or Capital Gains Tax
                than the amount of Gift Aid claimed on all my donations in that tax year, it is my
                responsibility to pay any difference.
              </span>
            </label>
            <p className="hint">
              Please tell the association if you stop paying enough tax, change your name or home
              address, or want to cancel this declaration.
            </p>
          </div>
        ) : null}
      </section>

      {eligibleForLogin ? (
        <section className="card">
          <h2>Your account</h2>
          <p className="muted small">
            Choose a password to sign in and see your membership details online. Leave blank if
            you'd rather not — you can still be a member without one.
          </p>
          <Field
            label="Password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            hint="At least 10 characters, if you set one."
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
          />
        </section>
      ) : null}

      <section className="card">
        <h2>Anything else?</h2>
        <div className="field">
          <label htmlFor="join-comments">Comments</label>
          <textarea
            id="join-comments"
            rows={3}
            value={form.comments}
            onChange={(e) => set('comments', e.target.value)}
          />
        </div>
      </section>

      <section className="card join-total">
        <div className="join-total-row">
          <span>Membership ({category?.name ?? 'choose a type above'})</span>
          <span>{category ? formatMoney(category.amount) : '—'}</span>
        </div>
        {category?.carries_bdi ? (
          <div className="join-total-row">
            <span>BDI cover</span>
            <span>{bdiAmount === 'referral' ? 'To be confirmed' : bdiAmount !== null ? formatMoney(bdiAmount) : '—'}</span>
          </div>
        ) : null}
        {donationsTotal > 0 ? (
          <div className="join-total-row">
            <span>Donations</span>
            <span>{formatMoney(donationsTotal)}</span>
          </div>
        ) : null}
        <div className="join-total-row join-total-due">
          <span>Total due</span>
          <span>{formatMoney(runningTotal)}</span>
        </div>
      </section>

      <button type="submit" className="primary join-submit" disabled={busy || !category}>
        {busy ? 'Submitting…' : 'Submit application'}
      </button>
    </form>
  );
}
